import { BotIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { dates } from "@/lib/dates";
import { email as emailUtils } from "@/lib/email";
import { cn } from "@/lib/utils";
import { EmailDirection, Email } from "@/types";

export const EmailCard = ({
  candidateName,
  email,
}: {
  candidateName: string;
  email: Email;
}) => {
  /* Context */
  const { userNameMap } = useTaggableUsers();

  /* Helpers */
  const isInbound = email.direction === EmailDirection.INCOMING;
  const isAgent = email.sentByAgent && !isInbound;
  const senderLabel = isInbound
    ? candidateName
    : (userNameMap[email.userId] ?? "Staffer").trim();
  const timestamp = dates.formatShortDateTime(email.sentAt);
  const cleanBody = emailUtils.stripQuotedReply(email.bodyText);

  const render = () => (
    <Column
      className={cn(
        "gap-1 rounded-md border p-3 shadow-sm",
        isInbound
          ? "ml-0 mr-6 border-slate-200 bg-white"
          : "ml-6 mr-0 border-slate-200 bg-slate-50",
        isAgent && "border-l-blue-300",
      )}
      style={isAgent ? { borderLeftWidth: 3 } : undefined}
    >
      <Row className="items-center justify-between gap-2">
        <Row className="min-w-0 items-center gap-1.5">
          <Text className="truncate text-xs">{senderLabel}</Text>
          {isAgent ? (
            <Row className="shrink-0 items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-1.5 py-0.5">
              <BotIcon className="h-2.5 w-2.5 text-blue-700" />
              <Text className="text-[10px] text-blue-700">AI</Text>
            </Row>
          ) : null}
        </Row>
        <Text className="shrink-0 text-[10px] font-normal text-slate-500">
          {timestamp}
        </Text>
      </Row>
      {cleanBody ? (
        <Text
          variant="secondary"
          className="whitespace-pre-wrap text-sm font-normal"
        >
          {cleanBody}
        </Text>
      ) : null}
    </Column>
  );

  return render();
};
